import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Eye,
  History,
  Info,
  MoreVertical,
  Pencil,
  Trash2,
  X,
} from "lucide-react";
import toast from "react-hot-toast";
import { salaryStructureService } from "../../../../services/api";
import { DsButton, DsSpinner } from "../../../../design-system";
import { usePermissions } from "../../../../rbac/PermissionsContext";
import StructureRevisionDeleteModal from "./StructureRevisionDeleteModal";

const SOURCE_LABELS = {
  joining: "Joining",
  revision: "Salary revision",
  manual_save: "Manual save",
  auto_sync: "Auto sync",
};

/** History SOURCE cell — never surface raw reimb codes (e.g. MOBILE_REIMBURSMENT). */
function formatSourceDisplay(entry) {
  const label = SOURCE_LABELS[entry?.source] || entry?.source || "—";
  const note = String(entry?.changeNote || "").trim();
  if (!note) return { text: label, title: label };

  if (entry?.source === "auto_sync" || /auto\s*sync\s*reimbursement/i.test(note)) {
    const tmpl =
      note.match(/from structure\s+"([^"]+)"/i)?.[1] ||
      note.match(/from structure\s+'([^']+)'/i)?.[1] ||
      null;
    const removed =
      /remove/i.test(note) || /no longer on template/i.test(note);
    const added = /add(ed)?\b/i.test(note) && !removed;
    const action = removed
      ? "removed reimbursements not on template"
      : added
        ? "added reimbursements from template"
        : "reimbursements aligned to template";
    const text = tmpl
      ? `Auto sync · ${tmpl} (${action})`
      : `Auto sync (${action})`;
    return { text, title: text };
  }

  // Strip legacy raw codes from any note shown in the grid
  const cleaned = note
    .replace(/\bMOBILE_REIMBURSMENT\b/gi, "Mobile reimbursement")
    .replace(/\b[A-Z][A-Z0-9_]{3,}_REIMBURSM?ENT\b/g, "reimbursement");
  const text = `${label} · ${cleaned}`;
  return { text, title: text };
}

const MONTH_ABBREV = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const MAX_COMPARE = 3;

function formatInr(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `₹${n.toLocaleString("en-IN")}`;
}

function formatDisplayDate(iso) {
  if (!iso) return "—";
  const raw = String(iso).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, d] = raw.split("-");
    const monthName = MONTH_ABBREV[Number(m) - 1] || m;
    return `${d} ${monthName} ${y}`;
  }
  return String(iso);
}

function parseMaybeJson(raw) {
  if (raw == null) return null;
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Flatten resolvedComponentsJson / summary into name → monthly for view/compare. */
function extractComponentRows(structureRow) {
  if (!structureRow) return [];
  const resolved = parseMaybeJson(structureRow.resolvedComponentsJson);
  const list = Array.isArray(resolved)
    ? resolved
    : Array.isArray(resolved?.components)
      ? resolved.components
      : [];
  const rows = list
    .map((c) => {
      const name = String(c.name || c.componentName || c.code || "").trim();
      if (!name) return null;
      const monthly = Math.abs(
        Number(c.monthlyValue ?? c.monthlyAmount ?? c.amount ?? c.monthly ?? 0) || 0
      );
      const category = String(c.category || c.type || c.section || "").toUpperCase();
      return { name, monthly, category, code: c.code || null };
    })
    .filter(Boolean);
  if (rows.length) return rows;

  const summary = parseMaybeJson(structureRow.computedSummaryJson) || {};
  const st = summary.statutory || summary;
  const fallback = [];
  if (Number(st.pfEmployee) > 0) {
    fallback.push({
      name: "PF (Employee)",
      monthly: Math.round((Number(st.pfEmployee) / 12) * 100) / 100,
      category: "DEDUCTION",
    });
  }
  if (Number(st.pfEmployer) > 0) {
    fallback.push({
      name: "PF (Employer)",
      monthly: Math.round((Number(st.pfEmployer) / 12) * 100) / 100,
      category: "EMPLOYER_BENEFIT",
    });
  }
  return fallback;
}

function statusBadgeClass(status) {
  if (status === "active") {
    return "bg-[hsl(var(--ds-success)/0.12)] text-[hsl(var(--ds-success))]";
  }
  if (status === "draft") {
    return "bg-[hsl(var(--ds-warning)/0.12)] text-[hsl(var(--ds-warning))]";
  }
  return "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]";
}

/**
 * Structure revision history — table grid, eye split-pane, compare (max 3),
 * ⋮ edit/delete for active + latest only.
 */
export default function EmployeeStructureHistory({
  isOpen,
  onClose,
  employeeId,
  employeeName = "",
  employeeCode = "",
  darkMode = false,
  onStructureChanged,
  onEditActive,
}) {
  const { hasScreenAccess, isAdmin } = usePermissions();
  const canDelete =
    isAdmin || hasScreenAccess("payroll", "assigned_structure", "delete");
  const canEdit =
    isAdmin ||
    hasScreenAccess("payroll", "assigned_structure", "edit") ||
    hasScreenAccess("payroll", "assigned_structure", "create");

  const [timeline, setTimeline] = useState([]);
  const [structuresById, setStructuresById] = useState({});
  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [viewId, setViewId] = useState(null);
  const [compareIds, setCompareIds] = useState([]);
  const [menu, setMenu] = useState(null);
  const menuRef = useRef(null);

  const fetchTimeline = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const [tlRes, listRes] = await Promise.all([
        salaryStructureService.getStructureTimeline(employeeId),
        salaryStructureService.listEmployeeStructures(employeeId),
      ]);
      setTimeline(Array.isArray(tlRes?.data) ? tlRes.data : []);
      const map = {};
      (Array.isArray(listRes?.data) ? listRes.data : []).forEach((s) => {
        map[Number(s.id)] = s;
      });
      setStructuresById(map);
    } catch (err) {
      console.error("Failed to load structure timeline", err);
      toast.error("Failed to load structure history");
      setTimeline([]);
      setStructuresById({});
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    if (!isOpen || !employeeId) return;
    fetchTimeline();
    setViewId(null);
    setCompareIds([]);
    setMenu(null);
  }, [isOpen, employeeId, fetchTimeline]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !deleteTarget) {
        if (menu) setMenu(null);
        else onClose?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, deleteTarget, onClose, menu]);

  useEffect(() => {
    if (!menu) return undefined;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menu]);

  const handleDeleted = () => {
    fetchTimeline();
    onStructureChanged?.();
    setViewId(null);
    setCompareIds([]);
  };

  const displayTimeline = useMemo(() => {
    const out = [];
    const seenEff = new Set();
    for (const entry of timeline) {
      const eff = String(entry.effectiveFrom || "").slice(0, 10);
      if (seenEff.has(eff)) continue;
      seenEff.add(eff);
      out.push(entry);
    }
    return out;
  }, [timeline]);

  const toggleCompare = (structureId) => {
    const id = Number(structureId);
    setCompareIds((prev) => {
      let next;
      if (prev.includes(id)) next = prev.filter((x) => x !== id);
      else if (prev.length >= MAX_COMPARE) {
        toast.error(`Select at most ${MAX_COMPARE} revisions to compare`);
        return prev;
      } else next = [...prev, id];
      if (next.length >= 2) setViewId(null);
      return next;
    });
  };

  const openMenu = (e, entry) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setMenu({
      structureId: entry.structureId,
      top: rect.bottom + 4,
      left: Math.min(rect.right - 160, window.innerWidth - 180),
      entry,
    });
  };

  const handleEdit = (entry) => {
    setMenu(null);
    if (entry.status === "active") {
      onEditActive?.(entry);
      onClose?.();
      toast.success("Active structure is open in the salary structure form");
      return;
    }
    if (entry.isLatest) {
      setViewId(entry.structureId);
      toast(
        "Latest revision is superseded — view on the right. Save from the active structure to create a new revision."
      );
      return;
    }
  };

  if (!isOpen || !employeeId) return null;

  const panel = darkMode
    ? "border-[hsl(var(--ds-border))] bg-[#1a1a1a] text-gray-100"
    : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]";

  const splitOpen = viewId != null || compareIds.length >= 2;
  const dialogWidth = splitOpen ? "min(100%,1180px)" : "min(100%,960px)";

  const viewEntry = displayTimeline.find((e) => Number(e.structureId) === Number(viewId));
  const viewRows = viewId ? extractComponentRows(structuresById[Number(viewId)]) : [];

  const compareEntries = compareIds
    .map((id) => displayTimeline.find((e) => Number(e.structureId) === id))
    .filter(Boolean);
  const compareMaps = compareEntries.map((e) => {
    const rows = extractComponentRows(structuresById[Number(e.structureId)]);
    const map = {};
    rows.forEach((r) => {
      map[r.name] = r.monthly;
    });
    return { entry: e, map, rows };
  });
  const allCompareNames = [
    ...new Set(compareMaps.flatMap((c) => Object.keys(c.map))),
  ].sort((a, b) => a.localeCompare(b));

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[10030] flex items-center justify-center bg-black/50 p-4"
        onClick={() => {
          if (!deleteTarget && !menu) onClose?.();
        }}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="structure-history-title"
          className={`flex h-[85%] w-[${dialogWidth}] max-w-[1180px] flex-col overflow-hidden rounded-xl border shadow-2xl ${panel}`}
          style={{ width: dialogWidth }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-3 border-b border-[hsl(var(--ds-border))] px-5 py-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 id="structure-history-title" className="text-base font-semibold">
                  Structure Revision History
                </h3>
                <span className="group relative inline-flex">
                  <button
                    type="button"
                    className="rounded-full p-0.5 text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
                    aria-label="About structure revision history"
                  >
                    <Info size={14} />
                  </button>
                  <span className="pointer-events-none absolute left-0 top-full z-20 mt-1 hidden w-72 rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-3 text-[11px] leading-relaxed text-[hsl(var(--ds-muted-fg))] shadow-lg group-hover:block group-focus-within:block">
                    One row per effective date. Use the eye to preview that version’s
                    components on the right. Tick up to {MAX_COMPARE} rows and open
                    Compare to see monthly amount differences. The ⋮ menu Edit/Delete
                    appears only for the active and latest revisions (delete still
                    requires newest-first).
                  </span>
                </span>
              </div>
              <p className="mt-0.5 text-xs text-[hsl(var(--ds-muted-fg))]">
                {employeeName || "Employee"}
                {employeeCode ? ` (${employeeCode})` : ""}
                {" · "}
                Table grid · preview · compare
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-1 opacity-70 hover:opacity-100"
              aria-label="Close history"
            >
              <X size={18} />
            </button>
          </div>

          <div className={`flex min-h-0 flex-1 ${splitOpen ? "flex-row" : "flex-col"}`}>
            <div
              className={`flex min-h-0 flex-col overflow-hidden px-5 py-4 ${
                splitOpen ? "w-[48%] border-r border-[hsl(var(--ds-border))]" : "w-full"
              }`}
            >
              {loading ? (
                <div className="flex items-center gap-2 py-8">
                  <DsSpinner className="h-6 w-6" />
                  <span className="text-sm text-[hsl(var(--ds-muted-fg))]">
                    Loading history…
                  </span>
                </div>
              ) : displayTimeline.length === 0 ? (
                <p className="text-sm text-[hsl(var(--ds-muted-fg))]">
                  No structure revision history found for this employee.
                </p>
              ) : (
                <>
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <p className="text-[11px] text-[hsl(var(--ds-muted-fg))]">
                      Compare selected: {compareIds.length}/{MAX_COMPARE}
                    </p>
                    <DsButton
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={compareIds.length < 2}
                      onClick={() => {
                        setViewId(null);
                      }}
                    >
                      {compareIds.length >= 2 ? "Show compare panel" : "Select 2–3 to compare"}
                    </DsButton>
                  </div>
                  <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-[hsl(var(--ds-border))]">
                    <table className="w-full min-w-[640px] border-collapse text-left text-sm">
                      <thead className="sticky top-0 z-[1] bg-[hsl(var(--ds-muted))] text-[11px] uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                        <tr>
                          <th className="px-2 py-2 font-medium">Cmp</th>
                          <th className="px-2 py-2 font-medium">Ver</th>
                          <th className="px-2 py-2 font-medium">CTC</th>
                          <th className="px-2 py-2 font-medium">Effective</th>
                          <th className="px-2 py-2 font-medium">Status</th>
                          <th className="px-2 py-2 font-medium">Source</th>
                          <th className="px-2 py-2 font-medium text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayTimeline.map((entry) => {
                          const id = Number(entry.structureId);
                          const showMenu = entry.status === "active" || entry.isLatest;
                          const selectedView = Number(viewId) === id;
                          return (
                            <tr
                              key={id}
                              className={`border-t border-[hsl(var(--ds-border))] ${
                                selectedView
                                  ? "bg-[hsl(var(--ds-primary)/0.08)]"
                                  : "hover:bg-[hsl(var(--ds-muted)/0.5)]"
                              }`}
                            >
                              <td className="px-2 py-2">
                                <input
                                  type="checkbox"
                                  checked={compareIds.includes(id)}
                                  onChange={() => toggleCompare(id)}
                                  aria-label={`Compare v${entry.version}`}
                                  className="h-3.5 w-3.5"
                                />
                              </td>
                              <td className="px-2 py-2 font-medium">
                                v{entry.version}
                                {entry.isLatest ? (
                                  <span className="ml-1 rounded-full bg-[hsl(var(--ds-primary)/0.12)] px-1.5 py-0.5 text-[9px] font-semibold text-[hsl(var(--ds-primary))]">
                                    Latest
                                  </span>
                                ) : null}
                              </td>
                              <td className="px-2 py-2 whitespace-nowrap">
                                {formatInr(entry.targetAnnualCtc)}
                              </td>
                              <td className="px-2 py-2 whitespace-nowrap">
                                {formatDisplayDate(entry.effectiveFrom)}
                              </td>
                              <td className="px-2 py-2">
                                <span
                                  className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${statusBadgeClass(
                                    entry.status
                                  )}`}
                                >
                                  {entry.status}
                                </span>
                              </td>
                              <td
                                className="max-w-[220px] truncate px-2 py-2 text-xs text-[hsl(var(--ds-muted-fg))]"
                                title={formatSourceDisplay(entry).title}
                              >
                                {formatSourceDisplay(entry).text}
                              </td>
                              <td className="px-2 py-2">
                                <div className="flex items-center justify-end gap-0.5">
                                  <button
                                    type="button"
                                    title="Preview structure"
                                    aria-label={`Preview v${entry.version}`}
                                    onClick={() => {
                                      setCompareIds([]);
                                      setViewId(id);
                                    }}
                                    className="rounded-md p-1.5 text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-fg))]"
                                  >
                                    <Eye size={15} />
                                  </button>
                                  {showMenu ? (
                                    <button
                                      type="button"
                                      aria-label="Row actions"
                                      aria-haspopup="menu"
                                      onClick={(e) => openMenu(e, entry)}
                                      className="rounded-md p-1.5 text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
                                    >
                                      <MoreVertical size={15} />
                                    </button>
                                  ) : null}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>

            {splitOpen ? (
              <div className="flex min-h-0 w-[52%] flex-col overflow-hidden px-4 py-4">
                {compareIds.length >= 2 && !viewId ? (
                  <>
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <h4 className="text-sm font-semibold">Compare revisions</h4>
                      <button
                        type="button"
                        className="text-xs text-[hsl(var(--ds-muted-fg))] hover:underline"
                        onClick={() => setCompareIds([])}
                      >
                        Clear selection
                      </button>
                    </div>
                    <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-[hsl(var(--ds-border))]">
                      <table className="w-full border-collapse text-left text-xs">
                        <thead className="sticky top-0 bg-[hsl(var(--ds-muted))]">
                          <tr>
                            <th className="px-2 py-2 font-medium">Component</th>
                            {compareMaps.map(({ entry }) => (
                              <th
                                key={entry.structureId}
                                className="px-2 py-2 font-medium whitespace-nowrap"
                              >
                                v{entry.version}
                                <span className="block font-normal text-[10px] text-[hsl(var(--ds-muted-fg))]">
                                  {formatDisplayDate(entry.effectiveFrom)}
                                </span>
                              </th>
                            ))}
                            {compareMaps.length === 2 ? (
                              <th className="px-2 py-2 font-medium">Δ</th>
                            ) : null}
                          </tr>
                        </thead>
                        <tbody>
                          {allCompareNames.map((name) => {
                            const vals = compareMaps.map((c) => Number(c.map[name]) || 0);
                            const allSame = vals.every((v) => Math.abs(v - vals[0]) < 0.005);
                            const delta =
                              compareMaps.length === 2
                                ? round2Delta(vals[1] - vals[0])
                                : null;
                            return (
                              <tr
                                key={name}
                                className={`border-t border-[hsl(var(--ds-border))] ${
                                  allSame ? "" : "bg-[hsl(var(--ds-warning)/0.08)]"
                                }`}
                              >
                                <td className="px-2 py-1.5 font-medium">{name}</td>
                                {vals.map((v, i) => (
                                  <td key={i} className="px-2 py-1.5 whitespace-nowrap">
                                    {formatInr(v)}
                                  </td>
                                ))}
                                {delta != null ? (
                                  <td
                                    className={`px-2 py-1.5 whitespace-nowrap ${
                                      Math.abs(delta) >= 0.01
                                        ? "font-medium text-[hsl(var(--ds-warning))]"
                                        : "text-[hsl(var(--ds-muted-fg))]"
                                    }`}
                                  >
                                    {delta === 0
                                      ? "—"
                                      : `${delta > 0 ? "+" : ""}${formatInr(delta)}`}
                                  </td>
                                ) : null}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : viewId ? (
                  <>
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <h4 className="text-sm font-semibold">
                        Preview · v{viewEntry?.version ?? "—"}
                        <span className="ml-2 text-xs font-normal text-[hsl(var(--ds-muted-fg))]">
                          Effective {formatDisplayDate(viewEntry?.effectiveFrom)}
                        </span>
                      </h4>
                      <button
                        type="button"
                        className="rounded-full p-1 opacity-70 hover:opacity-100"
                        aria-label="Close preview"
                        onClick={() => setViewId(null)}
                      >
                        <X size={16} />
                      </button>
                    </div>
                    {viewEntry?.changeNote ? (
                      <p className="mb-2 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                        {formatSourceDisplay(viewEntry).title}
                      </p>
                    ) : null}
                    <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-[hsl(var(--ds-border))]">
                      {viewRows.length === 0 ? (
                        <p className="p-4 text-sm text-[hsl(var(--ds-muted-fg))]">
                          No component breakdown stored for this revision.
                        </p>
                      ) : (
                        <table className="w-full border-collapse text-left text-xs">
                          <thead className="sticky top-0 bg-[hsl(var(--ds-muted))]">
                            <tr>
                              <th className="px-3 py-2 font-medium">Component</th>
                              <th className="px-3 py-2 font-medium">Category</th>
                              <th className="px-3 py-2 font-medium text-right">Monthly</th>
                            </tr>
                          </thead>
                          <tbody>
                            {viewRows.map((r) => (
                              <tr
                                key={`${r.code || r.name}`}
                                className="border-t border-[hsl(var(--ds-border))]"
                              >
                                <td className="px-3 py-1.5">{r.name}</td>
                                <td className="px-3 py-1.5 text-[hsl(var(--ds-muted-fg))]">
                                  {r.category || "—"}
                                </td>
                                <td className="px-3 py-1.5 text-right whitespace-nowrap">
                                  {formatInr(r.monthly)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="flex justify-end gap-2 border-t border-[hsl(var(--ds-border))] px-5 py-3">
            <DsButton type="button" variant="outline" size="sm" onClick={fetchTimeline} disabled={loading}>
              Refresh
            </DsButton>
            <DsButton type="button" variant="primary" size="sm" onClick={onClose}>
              Close
            </DsButton>
          </div>
        </div>
      </div>

      {menu
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              style={{ position: "fixed", top: menu.top, left: menu.left }}
              className="z-[10040] w-44 rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] py-1 shadow-lg"
            >
              {canEdit && (menu.entry.status === "active" || menu.entry.isLatest) ? (
                <button
                  type="button"
                  role="menuitem"
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[hsl(var(--ds-muted))]"
                  onClick={() => handleEdit(menu.entry)}
                >
                  <Pencil size={14} />
                  Edit
                </button>
              ) : null}
              {canDelete && menu.entry.canDelete ? (
                <button
                  type="button"
                  role="menuitem"
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[hsl(var(--ds-danger))] hover:bg-[hsl(var(--ds-muted))]"
                  onClick={() => {
                    setDeleteTarget({
                      structureId: menu.entry.structureId,
                      salaryRevisionId: menu.entry.salaryRevisionId,
                    });
                    setMenu(null);
                  }}
                >
                  <Trash2 size={14} />
                  Delete
                </button>
              ) : canDelete && menu.entry.deleteBlockReason ? (
                <p className="px-3 py-2 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                  {menu.entry.deleteBlockReason}
                </p>
              ) : null}
            </div>,
            document.body
          )
        : null}

      <StructureRevisionDeleteModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        employeeId={employeeId}
        structureId={deleteTarget?.structureId}
        salaryRevisionId={deleteTarget?.salaryRevisionId}
        onDeleted={handleDeleted}
      />
    </>,
    document.body
  );
}

function round2Delta(n) {
  return Math.round(Number(n || 0) * 100) / 100;
}

/** Footer trigger matching Salary Revision → View History */
export function StructureHistoryTriggerButton({
  onClick,
  disabled = false,
  loading = false,
  darkMode = false,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
        darkMode
          ? "border border-[#4C4C5C] bg-[#2C2C2C] text-gray-200 hover:bg-[hsl(var(--ds-surface))]"
          : "border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
      }`}
    >
      <History size={14} />
      {loading ? "Loading…" : "View History"}
    </button>
  );
}
